import { INITIAL_TEAM_NAVIGATION } from '@atom/hooks/useTeamNavigation';
import { INITIAL_INPUT } from '@atom/hooks/useWorkOrdersFilters';
import { INITIAL_WORK_TEMPLATES_VIEW } from '@atom/hooks/useWorkTemplatesView';
import { INITIAL_WORK_VALIDATIONS } from '@atom/hooks/useWorkValidations';
import defaultProfileImage from '@atom/images/defaultProfileImage.png';
import { Portal } from '@atom/types/portals';
import { ClusterDensity } from '@atom/types/preferences';
import { ReduxStore } from '@atom/types/store';
import configuration, { LOCAL } from '@atom/utilities/configuration';

// LOCAL_MOCK: when running against localhost (no backend), pre-populate the
// authenticated user so AuthorizedRouteV6 lets us through without an API call.
// See also: AuthorizedRouteV6.tsx and utilities/interceptors.ts.
const LOCAL_MOCK = configuration.env === LOCAL;

const mockPreferences = {
  landingPortal: Portal.DASHBOARD,
  budgeting: {
    hasBudgeting: true,
    hideCostType: false,
    showApprovalFlow: true,
    showAuthorizedBudget: true,
    showBudgetTypes: true,
    showExpenditures: true,
  },
  workOrders: {
    taskRenamingDisabled: false,
    completedDateEnabled: true,
  },
  inventory: {
    assets: {},
    budgetUnits: [],
    categories: {
      columns: [
        { name: 'Name', fieldName: 'name', dataType: 'shorttext' },
        { name: 'Asset Type', fieldName: 'assetType', dataType: 'shorttext' },
        { name: 'Path', fieldName: 'path', dataType: 'shorttext' },
      ],
    },
    moveElementEnabled: true,
    replaceElementEnabled: true,
  },
  map: {
    center: { lat: 33.749, lng: -84.388 }, // Atlanta, GA — generic default
    clusterDensity: ClusterDensity.default,
    kml: [],
    zoom: 10,
  },
  ai: { enabled: false },
  authentication: { connections: [] },
  formModules: [],
  reports: [],
  teams: {},
  timeTracking: {
    computedBudgets: false,
    timesheet: false,
    typeEnumeration: [],
  },
};

const auth = {
  isVerified: false,
  isLoggedIn: false,
  permissions: [],
};

const exportTemplateDetail = {
  id: '',
  name: '',
  createdBy: '',
  createdDate: 0,
  updatedBy: '',
  updatedDate: 0,
  isPublished: false,
  categoryIds: [],
  schemas: {},
};

const formInstance = {
  id: '',
  name: '',
  createdDate: 0,
  createdBy: '',
  pages: [
    {
      id: '1',
      name: 'Page 1',
      fields: {},
      order: [],
    },
  ],
  media: {},
};

const formTemplateDetail = {
  id: '',
  name: '',
  createdDate: 0,
  createdBy: '',
  pages: [
    {
      id: '1',
      name: 'Page 1',
      fields: {},
      order: [],
    },
  ],
};

const inventory = {
  categories: {
    inventoryPortalPath: {
      id: 'inventory',
      path: 'inventory',
    },
    inventoryCategory: '',
    inventoryCategoryTree: {
      id: 'inventory',
      name: 'Inventory',
      assetType: '',
      expanded: false,
      children: [],
      hasChildren: true,
    },
  },
  assets: {
    parentCategoryId: 'inventory',
    total: 0,
  },
  data: [],
  columns: [],
  filters: {},
  sort: {},
};

const inventoryFilterCategoryTree = [
  {
    id: 'inventory',
    name: 'Inventory',
    hasCategories: true,
    expanded: false,
  },
];

const loading = {
  loadingTenantCreation: false,
  loadingInventory: false,
  loadingUserLogin: false,
  loadingOrganizations: false,
  loadingUserRegistration: false,
  retrievedUser: false,
  loadingInventoryAssetDetail: true,
  loadingInventoryAssetAttributes: [],
  loadingInventoryElementDetail: false,
  loadingInventoryElementAttributes: [],
  loadingInventorySchema: true,
  loadingInventorySchemas: false,
  loadingInventorySchemasByIds: true,
  loadingInventorySchemaTree: false,
  loadingElementBatchCreation: false,
  loadingMedia: false,
  loadingMediaFolders: false,
  loadingFormTemplateCreation: false,
  loadingFormTemplateUpdate: false,
  loadingFormTemplate: false,
  loadingFormTemplates: true,
  loadingFormInstance: true,
  loadingInventoryAssetTreeNode: false,
  loadingInventoryFilterCategoryTreeNode: false,
  loadingSchemaDetail: true,
  loadingSchemaTreeNode: false,
  savingFormInstance: false,
  loadingApplicationSearch: false,
  loadingCharts: true,
  loadingWorkTemplates: true,
  loadingWorkTemplateCreation: false,
  loadingElementDelete: false,
  loadingMediaRename: false,
  loadingExportTemplates: false,
  loadingExportTemplateCreation: false,
  loadingExportTemplateDetail: false,
  loadingFullCategoryTree: false,
};

const mediaFolderTree = {
  id: 'root',
  name: 'All Files',
};

const mediaTotals = {
  document: { name: 'Documents', count: 0 },
  image: { name: 'Photos', count: 0 },
  video: { name: 'Videos', count: 0 },
};

const userProfile = LOCAL_MOCK
  ? {
      // Aligned to seed user-001 (Tyler Pauley) so the logged-in demo user IS
      // the creator/assignee of generated PM work — that's what lets auto-
      // generated and threshold-fired WOs surface in the "My Open Work Orders"
      // dashboard card (the assignee-notification channel for the demo).
      userId: 'user-001',
      firstName: 'Tyler',
      lastName: 'Pauley',
      department: 'IT / Atom',
      role: 'Admin',
      email: 'tyler.pauley@atom-ai.com',
      isActive: true,
      isVerified: true,
      // Matches seed user-001's photoUrl (photoByUserId in mocks/dot/data/users)
      // so the topbar avatar is the same face shown on Tyler's work orders.
      profileImage: 'https://randomuser.me/api/portraits/men/32.jpg',
      location: {
        type: 'Point',
        coordinates: [0, 0],
      },
      phoneNumber: '',
      title: 'Atom System Administrator',
    }
  : {
      userId: '',
      firstName: '',
      lastName: '',
      department: '',
      role: '',
      email: '',
      isActive: false,
      isVerified: false,
      profileImage: defaultProfileImage,
      location: {
        type: 'Point',
        coordinates: [0, 0],
      },
      phoneNumber: '',
      title: '',
    };

const initialState: ReduxStore = {
  auth,
  exportTemplateDetail,
  exportTemplates: [],
  formInstance,
  formTemplateDetail,
  formTemplates: [],
  formTemplatesTotal: 0,
  inventory,
  inventoryAssetDetail: {},
  inventoryFilterCategoryTree,
  inventoryFullCategoryTree: {},
  inventoryNavigation: {},
  // @ts-ignore
  inventorySchema: {},
  // @ts-ignore
  inventorySchemaDetail: {},
  inventorySchemas: [],
  inventorySchemasByIds: {},
  inventorySchemaTree: {},
  inventoryTotal: 0,
  loading,
  media: [],
  mediaFolders: [],
  mediaFolderTree,
  mediaTotal: 0,
  // @ts-ignore
  mediaTotals,
  organizations: [],
  poolAttributes: [],
  // @ts-ignore
  preferences: LOCAL_MOCK ? mockPreferences : {},
  teamNavigation: INITIAL_TEAM_NAVIGATION,
  // @ts-ignore
  userProfile,
  workOrdersInput: INITIAL_INPUT,
  workTemplates: [],
  workTemplatesTotal: 0,
  workTemplatesView: INITIAL_WORK_TEMPLATES_VIEW,
  workValidations: INITIAL_WORK_VALIDATIONS,
};

export default initialState;
